import { TeamMember, Agent, Traveler } from '@marageti/z4-sdk/lib/people';
import { CountryCodeMap, TimeZone } from '@marageti/z4-sdk/lib/standards';
import { PartnerOffice, Partner, PartnerCountry } from '@marageti/z4-sdk/lib/travel';
import {
  formatPhoneNumber,
  getTimeZoneCity,
  getFormattedDateLongMonthYYYY,
  getTimezoneNameAndGMTOffset,
} from '@marageti/z4-lib';

export const getFormattedPartnerCountries = (partner: Partner, countryCodes: CountryCodeMap) => {
  let str = '';
  if (partner?.primaryCountries?.length > 0) {
    str += countryCodes[partner.primaryCountries[0].country];
  }
  if (partner?.secondaryCountries?.length > 0) {
    str += ' · ';
    str += countryCodes[partner.secondaryCountries[0].country];
  }
  return str;
};

export const getCountryNames = (countries: any, countryCodes: any): string[] => {
  if (!countries || !countryCodes) return [];
  return countries.map((code: string) => countryCodes[code]);
};

export const formatCountryNames = (countries: any, countryCodes: any): string => {
  if (!countries || !countryCodes) return '-';
  return countries.map((code: string) => countryCodes[code]).join(', ');
};

export const getPhoneNumber = (person: Agent | TeamMember | Traveler | undefined): string | undefined => {
  if (!person) return undefined;
  if (!person.phone
    || !person.phone.number) {
    return '-';
  }
  return formatPhoneNumber(person.phone.prefix, person.phone.number);
};

export const getPartnerUSPhoneNumber = (partner: Partner | undefined): string | undefined => {
  if (!partner) return undefined;
  if (!partner.usPhone
    || !partner.usPhone.number) {
    return '-';
  }
  return formatPhoneNumber(partner.usPhone.prefix, partner.usPhone.number);
};

export const getPartnerPrimaryPhoneNumber = (partner: Partner | undefined): string | undefined => {
  if (!partner) return undefined;
  if (!partner.primaryPhone
    || !partner.primaryPhone.number) {
    return '-';
  }
  return formatPhoneNumber(partner.primaryPhone.prefix, partner.primaryPhone.number);
};

export const getPartnerOfficePhoneNumber = (office: PartnerOffice | undefined): string | undefined => {
  if (!office) return undefined;
  if (!office.phone
    || !office.phone.number) {
    return '-';
  }
  return formatPhoneNumber(office.phone.prefix, office.phone.number);
};

export const getTimeZone = (person: Agent | Traveler | TeamMember | undefined): string | undefined => {
  if (!person) return undefined;
  if (!person.timeZone?.name || typeof person.timeZone.rawOffsetInMinutes !== 'number') {
    return '-';
  }
  return getTimezoneNameAndGMTOffset(person.timeZone.name, person.timeZone.rawOffsetInMinutes);
};

export const formatTimeWithTimeZoneName = (timeZone: TimeZone | undefined, time?: string): string => {
  if (!timeZone || !timeZone.name || timeZone.name === '') return '-';
  const city = getTimeZoneCity(timeZone.name);
  return `${city} (Currently ${time || '-'})`;
};

export const getPersonTime = (person: Agent | Traveler | TeamMember | undefined, time?: string): string | undefined => {
  if (!person) return undefined;
  return formatTimeWithTimeZoneName(person.timeZone, time);
};

export const getHistory = (person: Agent | Traveler | TeamMember | undefined): string | undefined | any => {
  if (!person) return undefined;
  if (!person.createdAt) return '-';
  return getFormattedDateLongMonthYYYY(person.createdAt) || '';
};

export const getCountryOfResidence = (traveler: Traveler | undefined): string | undefined => {
  if (!traveler) return undefined;
  if (!traveler.countryOfResidence) return '-';
  return traveler.countryOfResidence;
};

export const getInitials = (name: string | undefined) => {
  if (!name || !name.trim()) {
    return '';
  }

  const nameParts = name.trim().split(' ');
  const firstInitial = nameParts[0]?.charAt(0).toUpperCase() || '';
  const secondInitial = nameParts[1]?.charAt(0).toUpperCase() || '';
  return (firstInitial + secondInitial).substring(0, 2);
};

export const getRatingNumber = (ratingPercentage: number | undefined): number => {
  if (!ratingPercentage) return 0;
  return Math.round(ratingPercentage / 20);
};

// This function compares the full names of two objects, and it used for sorting
// a list of names into alphabetical order
// The objects are expected to have a 'person' property with a 'fullName' string.
// The function returns -1 if the full name of the first object is alphabetically
// less than the full name of the second object, 1 if the full name of the first
// object is alphabetically greater than the full name of the second object,
// and 0 if the full names are equal.

// ***This function was refactored with ChatGPT.***
export const compareNames = (
  a: { fullName: string },
  b: { fullName: string },
) => (a.fullName.localeCompare(b.fullName));

export const formatArrayToString = (array: string[]) => {
  const lastIndex = array.length - 1;

  if (lastIndex === 0) {
    return array[0];
  } if (lastIndex === 1) {
    return `${array[0]} and ${array[1]}`;
  }
  const firstPart = array.slice(0, lastIndex).join(', ');
  return `${firstPart}, and ${array[lastIndex]}`;
};

// Use decimal as thousands separator for EUR only
export const formatCurrency = (amount: number, currencyCode: string, options?: Intl.NumberFormatOptions): string => {
  const formattedCurrency = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    ...options,
  }).format(amount);
  if (currencyCode === 'EUR') return formattedCurrency.replace(/[.,]/g, ((match) => (match === ',' ? '.' : ',')));
  if (currencyCode === 'GBP') formattedCurrency.replace('.', ',');
  return formattedCurrency;
};

/**
 * Merges two arrays of PartnerCountry and sorts them alphabetically by country name.
 *
 * @param primaryCountries - Array of primary PartnerCountry
 * @param secondaryCountries - Array of secondary PartnerCountry
 * @returns A new array containing all PartnerCountry sorted alphabetically by country
 */
export const mergeAndSortCountries = (
  primaryCountries: PartnerCountry[] = [],
  secondaryCountries: PartnerCountry[] = [],
): PartnerCountry[] => [...primaryCountries, ...secondaryCountries].sort((a, b) => a.country.localeCompare(b.country));

// Destination helpers
/**
 * Returns a normalized list of destination names.
 * - Safely handles null/undefined/non-array inputs
 * - Trims whitespace and filters out empty strings
 *
 * @param destinations Array of destination-like objects with optional `name` field
 * @returns string[] of cleaned, non-empty names
 */
export const getNormalizedDestinationNames = (
  destinations: Array<{ name?: string }> | null | undefined,
): string[] => {
  if (!Array.isArray(destinations)) return [];
  return destinations
    .map((d) => (d?.name || '').trim())
    .filter((name) => Boolean(name));
};

/**
 * Joins normalized destination names with a separator.
 *
 * @param destinations Array of destination-like objects with optional `name` field
 * @param separator Separator to use between names (default: ', ')
 * @returns Joined string of names; empty string if none
 */
export const joinDestinationNames = (
  destinations: Array<{ name?: string }> | null | undefined,
  separator: string = ', ',
): string => getNormalizedDestinationNames(destinations).join(separator);

/**
 * Formats a destination title from names using the pattern:
 *   "FirstName" or "FirstName and N+" (where N is remaining valid names)
 *
 * @param destinations Array of destination-like objects with optional `name` field
 * @returns Title string or empty string if no valid names
 */
export const formatDestinationTitle = (
  destinations: Array<{ name?: string }> | null | undefined,
): string => {
  const names = getNormalizedDestinationNames(destinations);
  if (names.length === 0) return '';
  const first = names[0];
  return `${first}${names.length > 1 ? ` and ${names.length - 1}+` : ''}`;
};
